import api from '../interceptors/axiosInterceptor'
import API_CONFIG from '../config'

const ANALYTICS_ENDPOINTS = {
  // آمار کلی
  OVERVIEW: '/analytics/overview',
  DASHBOARD: '/analytics/dashboard',

  // آمار فروش
  SALES: '/analytics/sales',
  SALES_BY_PERIOD: '/analytics/sales/period',
  SALES_BY_PRODUCT: '/analytics/sales/product',
  SALES_BY_CATEGORY: '/analytics/sales/category',
  SALES_BY_REGION: '/analytics/sales/region',

  // آمار کاربران
  USERS: '/analytics/users',
  USER_GROWTH: '/analytics/users/growth',
  USER_ACTIVITY: '/analytics/users/activity',
  USER_SEGMENTS: '/analytics/users/segments',

  // آمار محصولات
  PRODUCTS: '/analytics/products',
  TOP_PRODUCTS: '/analytics/products/top',
  PRODUCT_PERFORMANCE: '/analytics/products/performance',
  STOCK_ANALYTICS: '/analytics/products/stock',

  // آمار سفارشات
  ORDERS: '/analytics/orders',
  ORDER_METRICS: '/analytics/orders/metrics',
  CONVERSION_RATE: '/analytics/orders/conversion',

  // آمار بازدید
  VISITS: '/analytics/visits',
  PAGE_VIEWS: '/analytics/visits/pageviews',
  TRAFFIC_SOURCES: '/analytics/visits/sources',
  DEVICE_STATS: '/analytics/visits/devices',

  // آمار مالی
  REVENUE: '/analytics/revenue',
  REVENUE_BREAKDOWN: '/analytics/revenue/breakdown',
  PROFIT_MARGIN: '/analytics/revenue/profit',
  TAX_REPORT: '/analytics/revenue/tax',

  // گزارشات سفارشی
  CUSTOM_REPORT: '/analytics/reports/custom',
  SAVED_REPORTS: '/analytics/reports/saved',
  EXPORT_REPORT: '/analytics/reports/export',

  // پیش‌بینی‌ها
  FORECAST: '/analytics/forecast',
  TRENDS: '/analytics/trends',

  // اعلان‌های تحلیلی
  ALERTS: '/analytics/alerts',
  KPI: '/analytics/kpi'
}

const AnalyticsService = {
  // ========== آمار کلی ==========

  // دریافت خلاصه آمار
  async getOverview(timeRange = 'month') {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.OVERVIEW}`,
        { params: { range: timeRange } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // دریافت داده‌های داشبورد
  async getDashboardData(userId = null) {
    try {
      const params = userId ? { userId } : {}
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.DASHBOARD}`,
        { params }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار فروش ==========

  // آمار فروش
  async getSalesStats(startDate, endDate, filters = {}) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SALES}`,
        {
          params: {
            startDate,
            endDate,
            ...filters
          }
        }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // فروش بر اساس بازه زمانی
  async getSalesByPeriod(period = 'daily', startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SALES_BY_PERIOD}`,
        { params: { period, startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // فروش بر اساس محصول
  async getSalesByProduct(startDate, endDate, limit = 10) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SALES_BY_PRODUCT}`,
        { params: { startDate, endDate, limit } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // فروش بر اساس دسته‌بندی
  async getSalesByCategory(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SALES_BY_CATEGORY}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // فروش بر اساس منطقه
  async getSalesByRegion(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SALES_BY_REGION}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار کاربران ==========

  // آمار کاربران
  async getUserStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.USERS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // رشد کاربران
  async getUserGrowth(period = 'monthly', months = 12) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.USER_GROWTH}`,
        { params: { period, months } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // فعالیت کاربران
  async getUserActivity(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.USER_ACTIVITY}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // تقسیم‌بندی کاربران
  async getUserSegments() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.USER_SEGMENTS}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار محصولات ==========

  // آمار محصولات
  async getProductStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.PRODUCTS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // محصولات پرفروش
  async getTopProducts(limit = 10, period = 'month') {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.TOP_PRODUCTS}`,
        { params: { limit, period } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // عملکرد محصولات
  async getProductPerformance(productIds = [], startDate, endDate) {
    try {
      const response = await api.post(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.PRODUCT_PERFORMANCE}`,
        { productIds, startDate, endDate }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // تحلیل موجودی
  async getStockAnalytics() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.STOCK_ANALYTICS}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار سفارشات ==========

  // آمار سفارشات
  async getOrderStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.ORDERS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // معیارهای سفارش
  async getOrderMetrics(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.ORDER_METRICS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // نرخ تبدیل
  async getConversionRate(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.CONVERSION_RATE}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار بازدید ==========

  // آمار بازدید
  async getVisitStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.VISITS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // بازدید صفحات
  async getPageViews(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.PAGE_VIEWS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // منابع ترافیک
  async getTrafficSources(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.TRAFFIC_SOURCES}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // آمار دستگاه‌ها
  async getDeviceStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.DEVICE_STATS}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== آمار مالی ==========

  // درآمد
  async getRevenueStats(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.REVENUE}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // تجزیه درآمد
  async getRevenueBreakdown(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.REVENUE_BREAKDOWN}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // حاشیه سود
  async getProfitMargin(startDate, endDate) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.PROFIT_MARGIN}`,
        { params: { startDate, endDate } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // گزارش مالیاتی
  async getTaxReport(year, quarter = null) {
    try {
      const params = { year }
      if (quarter) params.quarter = quarter

      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.TAX_REPORT}`,
        { params }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== گزارشات سفارشی ==========

  // ایجاد گزارش سفارشی
  async createCustomReport(reportConfig) {
    try {
      const response = await api.post(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.CUSTOM_REPORT}`,
        reportConfig
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // دریافت گزارشات ذخیره شده
  async getSavedReports() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.SAVED_REPORTS}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // خروجی گرفتن از گزارش
  async exportReport(reportId, format = 'excel') {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.EXPORT_REPORT}/${reportId}`,
        {
          params: { format },
          responseType: 'blob'
        }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== پیش‌بینی‌ها ==========

  // پیش‌بینی فروش
  async getSalesForecast(periods = 6) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.FORECAST}`,
        { params: { periods } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // روندها
  async getTrends(metric = 'sales', period = 'monthly') {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.TRENDS}`,
        { params: { metric, period } }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== ابزارها و اعلان‌ها ==========

  // اعلان‌های تحلیلی
  async getAnalyticsAlerts() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.ALERTS}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // شاخص‌های کلیدی عملکرد
  async getKPIs() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${ANALYTICS_ENDPOINTS.KPI}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ========== ابزارهای کمکی ==========

  // فرمت‌دهی اعداد
  formatNumber(num, decimals = 0) {
    return new Intl.NumberFormat('fa-IR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num)
  },

  // فرمت‌دهی پول
  formatCurrency(amount, currency = 'ریال') {
    return `${this.formatNumber(amount, 0)} ${currency}`
  },

  // تولید رنگ برای چارت‌ها
  generateChartColors(count) {
    const colors = [
      '#4CAF50', '#2196F3', '#FF9800', '#E91E63',
      '#9C27B0', '#00BCD4', '#FF5722', '#795548',
      '#607D8B', '#3F51B5', '#009688', '#FFC107'
    ]
    return colors.slice(0, count)
  },

  // محاسبه درصد تغییر
  calculateChange(current, previous) {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  },

  handleError(error) {
    if (error.response) {
      return {
        message: error.response.data.message || 'خطا در دریافت آمار',
        status: error.response.status,
        data: error.response.data
      }
    } else if (error.request) {
      return {
        message: 'اتصال به سرور برقرار نشد',
        status: 0
      }
    } else {
      return {
        message: 'خطای ناشناخته رخ داده است',
        status: -1
      }
    }
  }
}

export default AnalyticsService